import React from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";

const today = new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });

const PrivacyPolicy = () => {
    return (
        <div className="theme-horizon" data-testid="privacy-policy-page">
            <SEO
                title="Privacy Policy · SouthTrip.in"
                description="How SouthTrip.in collects, uses and protects your personal data. Indian IT Act 2000 compliant privacy policy for our Kerala cab and tour services."
                path="/privacy-policy"
            />
            <article className="max-w-3xl mx-auto px-4 sm:px-6 py-16 lg:py-24 prose prose-zinc">
                <header className="mb-10">
                    <div className="hz-pill">Legal</div>
                    <h1 className="font-hz-display text-4xl md:text-5xl tracking-tight mt-4 leading-tight">
                        Privacy Policy
                    </h1>
                    <p className="text-[var(--ink-soft)] mt-3 text-sm">Last updated: {today}</p>
                </header>

                <section className="space-y-6 text-[var(--ink-soft)] leading-relaxed text-[15px] md:text-base">
                    <p>
                        SouthTrip.in (operated by <strong>LuxSaCabs</strong>, based at 35 P/N, Kavarapparambu Road,
                        Angamaly, Kerala 683572, India) respects your privacy and is committed to protecting your
                        personal data in accordance with the Information Technology Act, 2000 and the Information
                        Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or
                        Information) Rules, 2011.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">1. What data we collect</h2>
                    <p>When you book a cab, tour package or transfer through southtrip.in we collect:</p>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>Your full name</li>
                        <li>Phone number (with country code)</li>
                        <li>Email address</li>
                        <li>Pickup and drop-off locations</li>
                        <li>Travel dates and number of passengers</li>
                        <li>Any special requests (Hindi/Arabic driver, halal restaurant, child seat, etc.)</li>
                    </ul>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">2. How we use your data</h2>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>To confirm and operate your booking</li>
                        <li>To send booking confirmations and trip updates (email, WhatsApp, SMS)</li>
                        <li>To provide customer support 24/7</li>
                        <li>To send marketing communications (with explicit opt-in and easy opt-out via &quot;unsubscribe&quot;)</li>
                        <li>To improve our service through anonymous analytics</li>
                    </ul>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">3. Third-party services</h2>
                    <p>We share minimum necessary data with the following third-party services:</p>
                    <ul className="list-disc pl-6 space-y-1">
                        <li><strong>Google Analytics &amp; Google Tag Manager</strong> — for anonymous traffic analytics</li>
                        <li><strong>Google Ads</strong> — for measuring ad performance (conversion tracking)</li>
                        <li><strong>WhatsApp Business</strong> — for booking confirmations and customer support</li>
                        <li><strong>Resend</strong> — for transactional email delivery</li>
                        <li><strong>Our drivers</strong> — first name, phone number and trip details only</li>
                    </ul>
                    <p>We do <strong>not</strong> sell your personal data to any third party.</p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">4. Data retention</h2>
                    <p>
                        We retain your booking data for <strong>3 years</strong> after your last trip to comply with
                        Indian tax law (GST) and accounting obligations. After that period, your data is deleted
                        from our active systems. You may request earlier deletion at any time (see Section 6).
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">5. Cookies</h2>
                    <p>
                        We use essential cookies for session and language preferences, and optional analytics cookies
                        (Google Analytics) which respect your browser&apos;s Do Not Track signal.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">6. Your rights</h2>
                    <p>Under Indian law you have the right to:</p>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>Access the personal data we hold about you</li>
                        <li>Request correction of inaccurate data</li>
                        <li>Request deletion of your data (subject to legal retention obligations)</li>
                        <li>Withdraw consent to marketing at any time</li>
                    </ul>
                    <p>
                        To exercise any of these rights, email us at{" "}
                        <a href="mailto:info@southtrip.in" className="text-[var(--brand)] underline">info@southtrip.in</a>{" "}
                        from the email address linked to your booking.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">7. Data security</h2>
                    <p>
                        Your data is stored on secured servers with encrypted connections (HTTPS / TLS 1.3).
                        Access is limited to authorised SouthTrip staff. Payments are <strong>direct to driver</strong>;
                        we do not store any card or bank details.
                    </p>

                    <h2 className="font-hz-display text-2xl text-[var(--ink)] mt-10">8. Contact</h2>
                    <p>
                        For any privacy-related question or grievance, please contact:<br />
                        <strong>SouthTrip.in by LuxSaCabs</strong><br />
                        35 P/N, Kavarapparambu Road, Angamaly, Kerala 683572, India<br />
                        Email:{" "}
                        <a href="mailto:info@southtrip.in" className="text-[var(--brand)] underline">info@southtrip.in</a><br />
                        Phone: <a href="tel:+918075814740" className="text-[var(--brand)] underline">+91 80-75814740</a>
                    </p>

                    <p className="text-xs text-[var(--ink-softer)] pt-8 border-t border-[var(--border)] mt-12">
                        See also: <Link to="/terms" className="hz-link">Terms of Service</Link>{" · "}
                        <Link to="/refund-policy" className="hz-link">Refund Policy</Link>
                    </p>
                </section>
            </article>
        </div>
    );
};

export default PrivacyPolicy;
